import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchWithAuth } from "../../services/apiService";
import { getApiUrl } from "../../services/api";
import { setProfilePictureUrl } from "./userSlice";
import { tokenStore } from "../../services/tokenStore";
import { decodeJwt } from "../../services/jwt";
import { normalizeLanguageCode } from "../../utils/language";
import { createApiError } from "../../services/apiError";
import { withApiLanguageHeader } from "../../services/languageHeaders";

const AUTH_ROUTES = {
    register: "api/Auth/register",
    login: "api/Auth/login",
    externalLogin: "api/Auth/external-login",
    logout: "api/Auth/logout",

    emailConfirmation: "api/Auth/email-confirmation",
    passwordReset: "api/Auth/password-reset",
    language: "api/Auth/language",
};

const EMAIL_ALREADY_CONFIRMED = "EMAIL_ALREADY_CONFIRMED";

const claimsFromToken = (token) => {
    return decodeJwt(token) || { userId: null, role: null, userName: null };
};

const persistServerPrefs = (data) => {
    if ("preferredLanguage" in data) {
        localStorage.setItem("language", normalizeLanguageCode(data.preferredLanguage));
    }

    if ("showAdultContent" in data) {
        localStorage.setItem(
            "showAdultContent",
            JSON.stringify(data.showAdultContent ?? false)
        );
    }

    if ("websiteModeChoice" in data && data.websiteModeChoice != null) {
        localStorage.setItem("websiteModeChoice", data.websiteModeChoice);
    }
};

const applyAuthResponse = async (data, dispatch) => {
    tokenStore.set(data.token);
    persistServerPrefs(data);

    const { default: i18n } = await import("i18next");
    i18n.changeLanguage(normalizeLanguageCode(data.preferredLanguage));

    const claims = claimsFromToken(data.token);

    const me =
        data.user ||
        data.me ||
        {
            id: claims.userId,
            userName: claims.userName,
            role: claims.role || data.role || "guest",
            preferredLanguage: normalizeLanguageCode(data.preferredLanguage),
            showAdultContent: data.showAdultContent,
            websiteModeChoice: data.websiteModeChoice,
        };

    dispatch(setProfilePictureUrl(me?.profilePictureUrl || ""));

    return {
        role: me?.role || claims.role || data.role || "guest",
        user: me,
    };
};

export const register = createAsyncThunk(
    "auth/register",
    async (registerData, { rejectWithValue, dispatch }) => {
        try {
            const res = await fetch(getApiUrl(AUTH_ROUTES.register), {
                method: "POST",
                headers: withApiLanguageHeader({ "Content-Type": "application/json" }),
                credentials: "include",
                body: JSON.stringify(registerData),
            });

            if (!res.ok) {
                throw await createApiError(res, "Ошибка регистрации");
            }

            const data = await res.json();

            return await applyAuthResponse(data, dispatch);
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const login = createAsyncThunk(
    "auth/login",
    async (loginData, { rejectWithValue, dispatch }) => {
        try {
            const res = await fetch(getApiUrl(AUTH_ROUTES.login), {
                method: "POST",
                headers: withApiLanguageHeader({ "Content-Type": "application/json" }),
                credentials: "include",
                body: JSON.stringify(loginData),
            });

            if (!res.ok) {
                throw await createApiError(res, "Ошибка входа");
            }

            const data = await res.json();

            return await applyAuthResponse(data, dispatch);
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const externalLogin = createAsyncThunk(
    "auth/externalLogin",
    async ({ idToken }, { rejectWithValue, dispatch }) => {
        try {
            const res = await fetch(getApiUrl(AUTH_ROUTES.externalLogin), {
                method: "POST",
                headers: withApiLanguageHeader({ "Content-Type": "application/json" }),
                credentials: "include",
                body: JSON.stringify({ idToken }),
            });

            if (!res.ok) {
                throw await createApiError(res, "Ошибка входа через Google");
            }

            const data = await res.json();

            return await applyAuthResponse(data, dispatch);
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const refreshCurrentUser = createAsyncThunk(
    "auth/refreshCurrentUser",
    async (_, { rejectWithValue, dispatch, getState }) => {
        const sessionVersion = getState().auth.sessionVersion;
        try {
            const me = await fetchWithAuth("api/Auth/me", { method: "GET" });
            if (getState().auth.sessionVersion !== sessionVersion) {
                return rejectWithValue("Session changed");
            }
            dispatch(setProfilePictureUrl(me?.profilePictureUrl || ""));
            return me;
        } catch (error) {
            return rejectWithValue(error?.message || "Failed to refresh current user");
        }
    }
);

export const requestEmailConfirmation = createAsyncThunk(
    "auth/requestEmailConfirmation",
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await fetchWithAuth(getApiUrl(AUTH_ROUTES.emailConfirmation), {
                method: "POST",
            });
            return { response, me: null, alreadyConfirmed: false };
        } catch (error) {
            if (error?.code === EMAIL_ALREADY_CONFIRMED) {
                const me = await dispatch(refreshCurrentUser()).unwrap();
                return { response: null, me, alreadyConfirmed: true };
            }

            return rejectWithValue(error?.message || "Failed to send email confirmation");
        }
    }
);

export const linkGoogleAccount = createAsyncThunk(
    "auth/linkGoogleAccount",
    async ({ idToken }, { rejectWithValue, dispatch }) => {
        try {
            await fetchWithAuth("api/Auth/external-login/google/link", {
                method: "POST",
                body: JSON.stringify({ idToken }),
            });
            return await dispatch(refreshCurrentUser()).unwrap();
        } catch (error) {
            return rejectWithValue({
                code: error?.code || null,
                message: error?.message || "Failed to link Google account",
            });
        }
    }
);

export const logout = createAsyncThunk(
    "auth/logout",
    async (_, { dispatch }) => {
        try {
            const token = tokenStore.get();

            await fetch(getApiUrl(AUTH_ROUTES.logout), {
                method: "POST",
                headers: withApiLanguageHeader(token ? { Authorization: `Bearer ${token}` } : {}),
                credentials: "include",
            });
        } catch {
        } finally {
            tokenStore.clear();

            localStorage.removeItem("language");
            localStorage.removeItem("showAdultContent");
            localStorage.removeItem("websiteModeChoice");

            dispatch(setProfilePictureUrl(""));

            const connection = window.messageConnection;

            if (connection && connection.state === "Connected") {
                connection.stop().catch(() => { });
            }
        }
    }
);

const initialClaims = claimsFromToken(tokenStore.get());

const authSlice = createSlice({
    name: "auth",
    initialState: {
        isLoggedIn: !!tokenStore.get(),
        isLoading: false,
        isResolved: false,
        sessionVersion: 0,
        userRole: initialClaims.role || "guest",
        user: null,
        error: null,
    },
    reducers: {
        hydrateAuthState: (state, action) => {
            const { authenticated = false, me = null } = action.payload || {};
            const claims = claimsFromToken(tokenStore.get());

            state.sessionVersion += 1;
            state.isLoggedIn = authenticated;
            state.isResolved = true;
            state.userRole = authenticated
                ? me?.role || claims.role || "guest"
                : "guest";
            state.user = authenticated
                ? me || { id: claims.userId, role: claims.role, userName: claims.userName }
                : null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(register.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(register.fulfilled, (state, action) => {
                state.sessionVersion += 1;
                state.isLoading = false;
                state.isLoggedIn = true;
                state.isResolved = true;
                state.userRole = action.payload?.role || "guest";
                state.user = action.payload?.user || null;
                state.error = null;
            })
            .addCase(register.rejected, (state, action) => {
                state.isLoading = false;
                state.isResolved = true;
                state.error = action.payload;
            })

            .addCase(login.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.sessionVersion += 1;
                state.isLoading = false;
                state.isLoggedIn = true;
                state.isResolved = true;
                state.userRole = action.payload?.role || "guest";
                state.user = action.payload?.user || null;
                state.error = null;
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.isResolved = true;
                state.error = action.payload;
            })

            .addCase(externalLogin.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(externalLogin.fulfilled, (state, action) => {
                state.sessionVersion += 1;
                state.isLoading = false;
                state.isLoggedIn = true;
                state.isResolved = true;
                state.userRole = action.payload?.role || "guest";
                state.user = action.payload?.user || null;
                state.error = null;
            })
            .addCase(externalLogin.rejected, (state, action) => {
                state.isLoading = false;
                state.isResolved = true;
                state.error = action.payload;
            })

            .addCase(refreshCurrentUser.fulfilled, (state, action) => {
                state.user = action.payload;
                state.userRole = action.payload?.role || state.userRole;
            })

            .addCase(requestEmailConfirmation.fulfilled, (state, action) => {
                if (action.payload?.me) {
                    state.user = action.payload.me;
                    state.userRole = action.payload.me?.role || state.userRole;
                }
            })

            .addCase(linkGoogleAccount.fulfilled, (state, action) => {
                state.user = action.payload;
                state.userRole = action.payload?.role || state.userRole;
            })

            .addCase(logout.fulfilled, (state) => {
                state.sessionVersion += 1;
                state.isLoggedIn = false;
                state.isResolved = true;
                state.userRole = "guest";
                state.user = null;
                state.error = null;
            });
    },
});

export const { hydrateAuthState } = authSlice.actions;
export const hydrateAuth = (payload) => (dispatch) => {
    dispatch(hydrateAuthState(payload));

    const { authenticated = false, me = null } = payload || {};
    if (me || !authenticated) {
        dispatch(setProfilePictureUrl(me?.profilePictureUrl || ""));
    }
};

export default authSlice.reducer;
